import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Card,
  Table,
  Button,
  Space,
  Typography,
  Spin,
  message,
  Select,
  Row,
  Col,
  Statistic,
  Layout,
  Dropdown,
  Tag,
} from 'antd';
import {
  FileTextOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import 'dayjs/plugin/isoWeek';
import 'dayjs/plugin/isSameOrBefore';
import 'dayjs/plugin/isSameOrAfter';
import apiService from '../../services/api';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer, LineChart, CartesianGrid, Line, XAxis, YAxis } from 'recharts';

dayjs.extend(require('dayjs/plugin/isoWeek'));
dayjs.extend(require('dayjs/plugin/isSameOrBefore'));
dayjs.extend(require('dayjs/plugin/isSameOrAfter'));

const { Title, Text } = Typography;
const { Content } = Layout;

const JobReport = () => {
  const [allFetchedJobs, setAllFetchedJobs] = useState([]);
  const [filteredJobsForTable, setFilteredJobsForTable] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [plPositionTableFilter, setPlPositionTableFilter] = useState('all');
  const [channelTableFilter, setChannelTableFilter] = useState('all');
  const [vanTableFilter, setVanTableFilter] = useState('all');
  const [periodGrouping, setPeriodGrouping] = useState('monthly');

  const [stats, setStats] = useState({
    totalJobsInPeriod: 0,
    unassignedJobs: 0,
    assignedJobs: 0,
    completedJobs: 0,
    cancelledJobs: 0,
    uniqueCustomersInPeriod: 0,
    totalIncomeFromJobs: 0,
    totalExpenseFromJobs: 0,
  });

  const [jobsByPlPosition, setJobsByPlPosition] = useState({ income: 0, expense: 0 });
  const [jobsByChannelSummary, setJobsByChannelSummary] = useState([]);
  const [jobsOverTimeSummary, setJobsOverTimeSummary] = useState([]);

  const [channels, setChannels] = useState([]);
  const [vans, setVans] = useState([]);

  // Add color palette for channels
  const channelColors = [
    '#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#A28FD0', '#FF6699', '#FFB347', '#B0E57C', '#FF6F61', '#6B5B95', '#88B04B', '#F7CAC9', '#92A8D1', '#955251', '#B565A7', '#009B77', '#DD4124', '#D65076', '#45B8AC', '#EFC050'
  ];

  useEffect(() => {
    const fetchFilteredJobs = async () => {
      setLoading(true);
      try {
        // Build params based on dateFilter mode
        const params = {
          status: statusFilter === 'all' ? undefined : statusFilter,
          plPosition: plPositionTableFilter === 'all' ? undefined : plPositionTableFilter,
          channel: channelTableFilter === 'all' ? undefined : channelTableFilter,
          van: vanTableFilter === 'all' ? undefined : vanTableFilter,
          limit: 10000
        };
        const [jobsRes, channelsRes, vansRes] = await Promise.all([
          apiService.jobs.getAll(params),
          apiService.channels.getAll(),
          apiService.vans.getAll(),
        ]);
        setAllFetchedJobs(Array.isArray(jobsRes.data?.data) ? jobsRes.data.data : []);
        setChannels(channelsRes.data || []);
        setVans(vansRes.data || []);
      } catch (err) {
        message.error('Failed to fetch filtered data: ' + (err.response?.data?.message || err.message));
        setAllFetchedJobs([]);
        setChannels([]);
        setVans([]);
      } finally {
        setLoading(false);
      }
    };
    fetchFilteredJobs();
  }, [statusFilter, plPositionTableFilter, channelTableFilter, vanTableFilter]);

  const processJobData = useCallback(() => {
    setLoading(true);
    let jobsInDateRange = allFetchedJobs;
    // Remove client-side filtering for status, plPosition, channel, van
    // Only keep date range filtering for charts if needed, but jobsInDateRange is already filtered by backend

    let currentTotalIncome = 0;
    let currentTotalExpense = 0;
    const customerSet = new Set();
    const currentJobsByPl = { income: 0, expense: 0 };
    const currentJobsByChannel = {};
    const currentJobsOverTime = {};
    const currentJobStatusDist = { unassigned: 0, assigned: 0, completed: 0, cancelled: 0, other: 0 };

    jobsInDateRange.forEach(job => {
      if (job.customer?._id) customerSet.add(job.customer._id);
      const jobStatus = job.jobStatus?.toLowerCase() || 'other';
      currentJobStatusDist[jobStatus] = (currentJobStatusDist[jobStatus] || 0) + 1;

      if (job.plPosition === 'income') {
        currentJobsByPl.income++;
        currentTotalIncome += job.payment?.totalAmount || 0;
      } else if (job.plPosition === 'expense') {
        currentJobsByPl.expense++;
        currentTotalExpense += job.payment?.totalAmount || 0;
      }

      let channelName = 'Unknown';
      if (typeof job.channel === 'string') {
        if (/^[a-f0-9]{24}$/.test(job.channel)) {
          channelName = 'Unknown';
        } else {
          channelName = job.channel;
        }
      } else if (job.channel && typeof job.channel === 'object' && job.channel.name) {
        channelName = job.channel.name;
      }
      currentJobsByChannel[channelName] = (currentJobsByChannel[channelName] || 0) + 1;

      const jobDate = dayjs(job.date);
      let periodKey;
      switch (periodGrouping) {
        case 'daily': periodKey = jobDate.format('YYYY-MM-DD'); break;
        case 'weekly': periodKey = `${jobDate.year()}-W${jobDate.isoWeek()}`; break;
        case 'monthly':
        default: periodKey = jobDate.format('YYYY-MM'); break;
      }
      currentJobsOverTime[periodKey] = (currentJobsOverTime[periodKey] || 0) + 1;
    });

    setStats({
      totalJobsInPeriod: jobsInDateRange.length,
      unassignedJobs: currentJobStatusDist.unassigned || 0,
      assignedJobs: currentJobStatusDist.assigned || 0,
      completedJobs: currentJobStatusDist.completed || 0,
      cancelledJobs: currentJobStatusDist.cancelled || 0,
      uniqueCustomersInPeriod: customerSet.size,
      totalIncomeFromJobs: currentTotalIncome,
      totalExpenseFromJobs: currentTotalExpense,
    });
    setJobsByPlPosition(currentJobsByPl);
    setJobsByChannelSummary(Object.entries(currentJobsByChannel).map(([channel, count]) => ({ channel, count })).sort((a,b) => b.count - a.count));
    setJobsOverTimeSummary(Object.entries(currentJobsOverTime).map(([period, count]) => ({ period, count })).sort((a,b) => a.period.localeCompare(b.period)));

    // No client-side filtering for table
    setFilteredJobsForTable(jobsInDateRange);
    setLoading(false);
  }, [allFetchedJobs, periodGrouping]);

  useEffect(() => {
    if (allFetchedJobs.length > 0) {
      processJobData();
    }
  }, [allFetchedJobs, periodGrouping, processJobData]);

  const handleExport = async (format) => {
    try {
      const params = {
        format,
        status: statusFilter === 'all' ? undefined : statusFilter,
        plPosition: plPositionTableFilter === 'all' ? undefined : plPositionTableFilter,
        channel: channelTableFilter === 'all' ? undefined : channelTableFilter,
        van: vanTableFilter === 'all' ? undefined : vanTableFilter,
      };
      
      const response = await apiService.jobs.getReport(params);
      const contentType = format === 'excel' ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' : 'text/csv';
      const fileExtension = format === 'excel' ? 'xlsx' : 'csv';
      
      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `job_report.${fileExtension}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      message.success(`Report downloaded successfully in ${format.toUpperCase()} format`);
    } catch (err) {
      console.error('Export error:', err);
      message.error(err.response?.data?.message || `Failed to export job report as ${format.toUpperCase()}`);
    }
  };

  const exportMenuItems = [
    {
      key: 'csv',
      icon: <FileTextOutlined />,
      label: 'Export as CSV',
      onClick: () => handleExport('csv')
    },
    {
      key: 'excel',
      icon: <FileTextOutlined />,
      label: 'Export as Excel',
      onClick: () => handleExport('excel')
    }
  ];

  const columns = [
    {
      title: 'External Reference',
      dataIndex: 'bookingReference',
      key: 'bookingReference',
      render: (text, record) => (
        <button
          type="button"
          style={{ background: 'none', border: 'none', color: '#1890ff', textDecoration: 'underline', cursor: 'pointer', padding: 0 }}
          onClick={() => window.open(`/jobs/${record._id}`, '_blank')}
        >
          {text || 'No Reference'}
        </button>
      ),
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      sorter: (a, b) => dayjs(a.date).unix() - dayjs(b.date).unix(),
      defaultSortOrder: 'descend',
      render: (date) => date ? dayjs(date).format('DD/MM/YYYY') : 'N/A'
    },
    {
      title: 'Customer',
      dataIndex: 'customer',
      key: 'customer',
      render: (customer) => {
        if (!customer || typeof customer !== 'object') return 'N/A';
        return customer.name || 'N/A';
      }
    },
    {
      title: 'Status',
      dataIndex: 'jobStatus',
      key: 'jobStatus',
      render: (status) => {
        if (!status) return <Text>N/A</Text>;
        
        const statusColors = {
          'unassigned': 'warning',
          'assigned': 'processing',
          'completed': 'success',
          'cancelled': 'error'
        };
        
        const color = statusColors[status] || 'default';
        const displayStatus = status.charAt(0).toUpperCase() + status.slice(1);
        
        return (
          <Tag color={color}>
            {displayStatus}
          </Tag>
        );
      }
    },
    {
      title: 'Amount',
      dataIndex: 'payment',
      key: 'payment',
      render: (payment) => {
        if (!payment || typeof payment !== 'object') return '£0.00';
        return `£${payment.totalAmount?.toFixed(2) || '0.00'}`;
      }
    }
  ];

  if (loading && allFetchedJobs.length === 0) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <Layout>
      <Content style={{ padding: '24px' }}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div>
            <Title level={2}>Job Performance Report</Title>
            <Text type="secondary">Analyze job metrics, statuses, and distributions.</Text>
          </div>

          {loading && allFetchedJobs.length === 0 && <div style={{textAlign: 'center', padding: '20px'}}><Spin tip="Initialising report..."/></div>}
          {(!loading || allFetchedJobs.length > 0) && (
            <>
              <Card title="Filter Job List Options" style={{marginTop: 16}}>
                <Row gutter={[16, 24]} align="bottom">
                  <Col xs={24} sm={12} md={4}>
                    <Space direction="vertical" style={{width: '100%'}}><Text>Job Status:</Text>
                      <Select value={statusFilter} onChange={setStatusFilter} style={{ width: '100%' }}>
                        <Select.Option value="all">All Statuses</Select.Option>
                        <Select.Option value="unassigned">Unassigned</Select.Option>
                        <Select.Option value="assigned">Assigned</Select.Option>
                        <Select.Option value="completed">Completed</Select.Option>
                        <Select.Option value="cancelled">Cancelled</Select.Option>
                      </Select>
                    </Space>
                  </Col>
                  <Col xs={24} sm={12} md={4}>
                    <Space direction="vertical" style={{width: '100%'}}><Text>P/L Position:</Text>
                      <Select value={plPositionTableFilter} onChange={setPlPositionTableFilter} style={{ width: '100%' }}>
                        <Select.Option value="all">All P/L</Select.Option>
                        <Select.Option value="income">Income</Select.Option>
                        <Select.Option value="expense">Expense</Select.Option>
                      </Select>
                    </Space>
                  </Col>
                  <Col xs={24} sm={12} md={4}>
                     <Space direction="vertical" style={{width: '100%'}}><Text>Channel:</Text>
                      <Select value={channelTableFilter} onChange={setChannelTableFilter} style={{ width: '100%' }} showSearch filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}>
                        <Select.Option value="all">All Channels</Select.Option>
                        {channels.map(c => <Select.Option key={c._id} value={c.name}>{c.name}</Select.Option>)}
                      </Select>
                    </Space>
                  </Col>
                  <Col xs={24} sm={12} md={4}>
                    <Space direction="vertical" style={{width: '100%'}}><Text>Van:</Text>
                      <Select value={vanTableFilter} onChange={setVanTableFilter} style={{ width: '100%' }} showSearch filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}>
                        <Select.Option value="all">All Vans</Select.Option>
                        {vans.map(v => <Select.Option key={v._id} value={v._id}>{v.registration} ({v.make} {v.model})</Select.Option>)}
                      </Select>
                    </Space>
                  </Col>
                  <Col xs={24} sm={12} md={4}>
                    <Space direction="vertical" style={{width: '100%'}}><Text>Summary Grouping:</Text>
                      <Select value={periodGrouping} onChange={setPeriodGrouping} style={{ width: '100%' }}>
                        <Select.Option value="monthly">Monthly</Select.Option>
                        <Select.Option value="weekly">Weekly</Select.Option>
                        <Select.Option value="daily">Daily</Select.Option>
                      </Select>
                    </Space>
                  </Col>
                   <Col xs={24} sm={24} md={2}>
                  </Col>
                </Row>
              </Card>

              <Row gutter={[16, 16]}>
                <Col xs={12} sm={8} md={6} lg={4}><Card><Statistic title="Total Jobs (Period)" value={stats.totalJobsInPeriod} /></Card></Col>
                <Col xs={12} sm={8} md={6} lg={4}><Card><Statistic title="Unassigned" value={stats.unassignedJobs} valueStyle={{color: 'orange'}}/></Card></Col>
                <Col xs={12} sm={8} md={6} lg={4}><Card><Statistic title="Assigned" value={stats.assignedJobs} valueStyle={{color: 'blue'}} /></Card></Col>
                <Col xs={12} sm={8} md={6} lg={4}><Card><Statistic title="Completed" value={stats.completedJobs} valueStyle={{color: 'green'}} /></Card></Col>
                <Col xs={12} sm={8} md={6} lg={4}><Card><Statistic title="Cancelled" value={stats.cancelledJobs} valueStyle={{color: 'red'}} /></Card></Col>
              </Row>

              <Row gutter={[16, 16]} style={{marginTop: 16}}>
                <Col xs={24} md={8}>
                  <Card title="Jobs by P/L Position (Period)">
                    <div style={{ height: 300 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={[
                              { name: 'Income', value: jobsByPlPosition.income },
                              { name: 'Expense', value: jobsByPlPosition.expense },
                            ]}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={80}
                            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                          >
                            <Cell key="income" fill="#00C49F" />
                            <Cell key="expense" fill="#FF8042" />
                          </Pie>
                          <Tooltip formatter={(value, name) => [value, name]} />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </Card>
                </Col>
                <Col xs={24} md={8}>
                  <Card title="Jobs by Channel (Period)">
                    <div style={{ height: 300 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={jobsByChannelSummary}
                            dataKey="count"
                            nameKey="channel"
                            cx="50%"
                            cy="50%"
                            outerRadius={80}
                            label={({ name, percent, channel }) => `${channel || name}: ${(percent * 100).toFixed(1)}%`}
                          >
                            {jobsByChannelSummary.map((entry, idx) => (
                              <Cell key={`cell-${entry.channel}`} fill={channelColors[idx % channelColors.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value, name) => [value, name]} />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </Card>
                </Col>
                <Col xs={24} md={8}>
                  <Card title={`Jobs Over Time (${periodGrouping})`}>
                    <div style={{ width: '100%', overflowX: 'auto' }}>
                      <div style={{ width: Math.max(jobsOverTimeSummary.length * 60, 600) }}>
                        <ResponsiveContainer width="100%" height={300}>
                          <LineChart data={jobsOverTimeSummary.slice(-12)}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="period" angle={-45} textAnchor="end" height={60} />
                            <YAxis allowDecimals={false} />
                            <Tooltip />
                            <Legend />
                            <Line type="monotone" dataKey="count" name="Jobs" stroke="#8884d8" />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </Card>
                </Col>
              </Row>
              {loading && <div style={{textAlign: 'center', padding: '20px'}}><Spin tip="Processing data..."/></div>}
            </>
          )}

          <Card 
            title="Filtered Job List" 
            extra={
              <Space>
                <Link to="/jobs">View All Jobs</Link>
                <Dropdown menu={{ items: exportMenuItems }} placement="bottomRight">
                  <Button type="primary" icon={<DownloadOutlined />}>Export List</Button>
                </Dropdown>
              </Space>
            }
          >
            <Table
              columns={columns}
              dataSource={filteredJobsForTable.slice(0, 10)}
              loading={loading}
              rowKey="_id"
              pagination={false}
              scroll={{ x: 'max-content' }}
            />
          </Card>
        </Space>
      </Content>
    </Layout>
  );
};

export default JobReport; 